import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { PlatformRole, hasRole, useSession } from '../auth/session';
import { OnboardingScreen } from '../features/onboarding/OnboardingScreen';
import { BackLink } from '../components/BackLink';

type DirectoryOutcome = 'connected' | 'denied' | 'error';
type CloudflareOutcome = 'connected' | 'denied' | 'error';

/**
 * A sibling of the tenant detail page rather than a tab inside it.
 *
 * Onboarding is a long-running job worked across days, with a PowerShell session open next to it.
 * It deserves its own URL so a technician can keep it open, reload it, and send it to whoever is
 * covering the change window.
 */
export const Route = createFileRoute('/tenants_/$tenantId_/onboarding')({
  validateSearch: (
    search: Record<string, unknown>,
  ): { directory?: DirectoryOutcome; cloudflare?: CloudflareOutcome; reason?: string } => ({
    directory:
      search.directory === 'connected' || search.directory === 'denied' || search.directory === 'error'
        ? search.directory
        : undefined,
    cloudflare:
      search.cloudflare === 'connected' || search.cloudflare === 'denied' || search.cloudflare === 'error'
        ? search.cloudflare
        : undefined,
    reason: typeof search.reason === 'string' ? search.reason : undefined,
  }),
  component: OnboardingPage,
});

function OnboardingPage() {
  const { t } = useTranslation(['onboarding', 'tenants']);
  const { tenantId } = Route.useParams();
  const { directory, cloudflare, reason } = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  const canView = hasRole(session, PlatformRole.Viewer, tenantId);
  // Every transition on this screen changes what happens to a customer's mail, so moving a tenant
  // through onboarding is a tenant-administrator action even though reading the state is not.
  const canAdvance = hasRole(session, PlatformRole.TenantAdmin, tenantId);

  useEffect(() => {
    if (!directory && !cloudflare) {
      if (reason) {
        notifications.show({
          color: 'red',
          message: reason,
        });
        void navigate({
          search: { directory: undefined, cloudflare: undefined, reason: undefined },
          replace: true,
        });
      }
      return;
    }

    if (directory === 'connected') {
      notifications.show({
        color: 'green',
        message: t('onboarding:directoryConnectedContinue'),
      });
      void queryClient.invalidateQueries({ queryKey: ['integrations', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['onboarding', tenantId] });
    } else if (directory === 'denied') {
      notifications.show({
        color: 'orange',
        message: reason ?? t('onboarding:adminConsentDenied'),
      });
    } else if (directory === 'error') {
      notifications.show({
        color: 'red',
        message: reason ?? t('onboarding:directoryConsentFailed'),
      });
    }

    if (cloudflare === 'connected') {
      notifications.show({
        color: 'green',
        message: t('onboarding:cf.connectedFlash'),
      });
      void queryClient.invalidateQueries({ queryKey: ['smtp-domain-cloudflare', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['smtp-domain-dns-status', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['smtp-domain', tenantId] });
    } else if (cloudflare === 'denied') {
      notifications.show({
        color: 'orange',
        message: reason ?? t('onboarding:cf.connectDenied'),
      });
    } else if (cloudflare === 'error') {
      notifications.show({
        color: 'red',
        message: reason ?? t('onboarding:cf.connectFailed'),
      });
    }

    void navigate({
      search: { directory: undefined, cloudflare: undefined, reason: undefined },
      replace: true,
    });
  }, [cloudflare, directory, navigate, queryClient, reason, t, tenantId]);

  return (
    <Stack gap="sm">
      <BackLink to="/tenants/$tenantId" params={{ tenantId }} search={{ tab: 'settings' }} />

      {!canView ? (
        <Alert color="red" title={t('onboarding:noAccessTitle')}>
          {t('onboarding:noAccessBody')}
        </Alert>
      ) : (
        <>
          {!canAdvance && (
            <Alert color="blue" variant="light" title={t('onboarding:readOnlyTitle')}>
              {t('onboarding:readOnlyBody')}
            </Alert>
          )}
          <OnboardingScreen
            tenantId={tenantId}
            canAdvance={canAdvance}
            onOpenTester={() => void navigate({ to: '/tenants/$tenantId/tester', params: { tenantId } })}
          />
        </>
      )}
    </Stack>
  );
}
